import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import Draggable from 'react-draggable';
import { Typography, Icon, useModal, ConfirmationPopup, Loader } from '@ohif/ui';
import classNames from 'classnames';
import RichTextEditor from './RichTextEditor';
import { _editorMode, _roleType } from '@custom/Utils/constants/appConstants';
import SpeechToText from '@custom/Common/SpeechToText';
import { useTheme } from "../../ui/src/contextProviders"
import voiceService from '@custom/services/voiceService';
import hospitalDetailApi from '@custom/rtk-apis/hospital/hospitalDetails';
import userDetails from '@custom/rtk-apis/user/userDetails';
import { AppDispatch } from '@custom/store/store';
import { useDispatch } from 'react-redux';
import redoScan from '@custom/rtk-apis/redo/redoScan';
import { servicesManager } from '../../../platform/app/src/App';
import { getCloudPACSToken } from '@custom/Utils/authUtils';
import { _deleteDicomUrl } from '@custom/Utils/constants/apiUrls';
import { useNavigate } from 'react-router-dom';

if (typeof document !== 'undefined') {
  ReactModal.setAppElement(document.getElementById('root'));
}

const Modal = ({
  closeButton,
  shouldCloseOnEsc = true,
  isOpen,
  title,
  onClose,
  children,
  shouldCloseOnOverlayClick = true,
  movable = false,
  containerDimensions = null,
  contentDimensions = null,
  onSave,
  onCancel,
  setIsConfirmingAuthorize,
  setReportComplete,
  setIsAuthorized,
  setSelectedReport,
  onAuthorize,
  handleInputChange,
  editMode = false,
  selectedReport,
  isAuthorized,
  isConfirmingAuthorize,
  updateReportInformation,
  getReports,
  setIsModalOpen
}) => {
  const { hide } = useModal();
  const { isDarkMode } = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const { uiNotificationService } = servicesManager.services;
  const [speechToTextAvailable, setSpeechToTextAvailable] = useState(false)
  const [isPopupRedoOpen, setIsPopupRedoOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleClose = () => {
    hide();
  };

  const handleRedo = () => {
    setReason('');
    setIsPopupRedoOpen(true);
  };

  const handleRedoConfirm = async () => {
    if (!reason.trim()) {
      setErrorMessage('Reason is required for redo scan.');
      uiNotificationService.show({
        title: 'Validation Error',
        message: 'Please provide a reason for the redo scan.',
        type: 'error',
        position: 'topRight',
      });
      return;
    }

    setIsPopupRedoOpen(false);
    setIsLoading(true);
    const payload = { status: 'Redo' };
    try {
      const response = await dispatch(redoScan({ patientID: selectedReport?.patientId, payload })).unwrap();
      if (response && response.status === 'SUCCESS') {
        setIsLoading(false);
        setErrorMessage('');
        uiNotificationService.show({
          title: 'Redo Scan Success',
          message: 'Rescan completed successfully.',
          type: 'success',
          position: 'topRight'
        });
        await handleConfirmDelete();
        if (getReports) {
          getReports(30, 1)
        }
        setIsModalOpen?.(false);
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (error) {
      setIsLoading(false);
      setErrorMessage('No Appointment Record Found for This Report.');
      uiNotificationService.show({
        title: 'Redo Scan Error',
        message: 'No Appointment Record Found for This Report.',
        type: 'error',
      });
    }
  };

  const handleConfirmDelete = async () => {
    const pacsToken = getCloudPACSToken();
    const studyInstanceUID = selectedReport?.studyInstanceUID;
    console.log(studyInstanceUID, "studyInstanceUID");
    try {
      const fetchPromises =
        fetch(_deleteDicomUrl(studyInstanceUID), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${pacsToken}`,
          },
        });

      const responses = await fetchPromises;

      if (!responses.ok) {
        throw new Error(
          `Failed to fetch some files. Statuses: ${responses.status}`
        );
      }

      uiNotificationService.show({
        title: "Success",
        message: "All files deleted successfully",
        type: "success",
        duration: 3000,
        position: "topRight",
      })
    } catch (error) {
      console.log(error)
      uiNotificationService.show({
        title: "Error",
        message: `Failed to delete files: ${error.message}`,
        type: "error",
        duration: 3000,
        position: "topRight",
      })
    }
  }

  const handleRedoCancel = () => {
    setReason('');
    setErrorMessage('');
    setIsPopupRedoOpen(false);
  };

  useEffect(() => {
    console.log('selectedReport', selectedReport);
  }, [selectedReport?.impressions]);

  useEffect(() => {
    // Check speech to text availability
    const checkSpeechToTextAvailability = async () => {
      try {
        const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');
        const [hospitalDetail, userDetail] = await Promise.all([
          dispatch(hospitalDetailApi({ hospitalId })).unwrap(),
          dispatch(userDetails()).unwrap()
        ]);
        setSpeechToTextAvailable(
          hospitalDetail?.result?.addOnsAvailable?.speechToText === true &&
          userDetail?.result?.speechToText === true
        );
      } catch (err) {
        console.error("Error in checkSpeechToTextService", err);
        setSpeechToTextAvailable(false);
      }
    };

    checkSpeechToTextAvailability();
  }, []);

  const handleCancel = () => {
    const socketState = voiceService.getSocketState();

    if (socketState === WebSocket.OPEN) {
      voiceService.stopStreaming();
    } else if (socketState === WebSocket.CONNECTING) {
      voiceService.deleteStreaming();
    } else {
      voiceService.deleteStreaming();
    }

    getReports?.();
    setIsModalOpen?.(false);
  };

  const checkPermissionByRole = () => {
    const role = sessionStorage.getItem('role') || localStorage.getItem('role');
    if (role === _roleType.pathologist || role === _roleType.radiologist || role === _roleType.hospitalSuperadmin || role === _roleType.admin) {
      return true;
    } else {
      return false;
    }
  }

  const renderHeader = () =>
    title && (
      <header className={classNames(!isDarkMode ? "bg-white" : "bg-primary-dark", isDarkMode ? 'border-white' : 'border-black', "drag-handle flex items-center rounded-tl rounded-tr px-[20px] pt-[20px] pb-[20px] border-b")} style={{
        maxWidth: '100%',
        margin: '0 auto',
      }}>
        <Typography
          variant="h6"
          color="primaryLight"
          className={!isDarkMode ? 'flex grow !leading-[1.2] text-black' : 'flex grow !leading-[1.2]'}
          data-cy="modal-header"
          style={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: '22px',
            fontWeight: 600,
            lineHeight: '32px',
            textAlign: 'left',
            paddingLeft: '20px'

          }}
        >
          {title}
        </Typography>
          {selectedReport?.modality !== 'SM' && (
              <button
                    className="btn btn__primary"
                  style={{ height: '35px', marginRight: '10px' }}
               onClick={handleRedo}
              >
           Redo Study
        </button>
         )}
        {title !== 'Authorize Report!' && closeButton && (
          <Icon
            onClick={handleCancel}
            name="close"
            className={`cursor-pointer ${isDarkMode ? 'text-white' : 'text-black'}`}
          />
        )}
      </header>
    );

  const renderFooterButtons = () => {
    if (title === "Authorize Report!") {
      return (
        <>

        </>
      );
    }
    else {

      return (
        <>
          <button
            type="button"
            onClick={() => {
              if (title === "Authorize Report") {
                setIsAuthorized(false);
              } else if (editMode) {
                setIsModalOpen(false);
              } else {
                setReportComplete(false);
              }

              if (typeof onCancel === 'function') {
                onCancel();
              }

            }}
            style={{
              background: '#FFFFFF',
              width: '90px',
              height: '44px',
              padding: '10px 16px',
              gap: '8px',
              borderRadius: '6px 0 0 0',
              border: '1px solid #000',
              //opacity: 0,
            }}
            className="bg-gray-500 text-black px-4 py-2 rounded-md hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              if (title === "Authorize Report") {
                setIsAuthorized(true);
              } else {
                onSave();
              }
            }}
            style={{ background: '#0F8491' }}
            className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
          >
            {title === "Authorize Report" ? "Authorize" : "Save"}
          </button>
        </>
      );
    }
  };


  const modalContent = (
    <>
        {title && renderHeader()}
        <section
          className={
            contentDimensions
              ? `ohif-scrollbar ${!isDarkMode ? 'bg-white' : 'bg-primary-dark'} overflow-y-auto ${contentDimensions}`
              : `ohif-scrollbar modal-content ${!isDarkMode ? 'bg-white' : 'bg-primary-dark'} overflow-y-auto  px-[20px] pt-2 pb-[8px]`
          }
          style={{
            maxWidth: '100%',
            margin: '0 auto',
            borderRadius: title === '' ? '32px' : undefined,
          }}
        >
          {isAuthorized || isConfirmingAuthorize ? (
            children
          ) : (
            <div id="content-edit">
              <RichTextEditor
                selectedItem={selectedReport}
                editorMode={selectedReport.editorMode}
                retractable={true}
                setSelectedReport={setSelectedReport}
                mode="ChangeStatus"
                getReports={getReports}
                setIsModalOpen={setIsModalOpen}
                findingsProp={selectedReport.findings}
                impressionsProp={selectedReport.impressions}
                commentprop={selectedReport?.comment}
                patientId={selectedReport.patientId}
                reportId={selectedReport._id}
                editMode={editMode}
                title={title}
                rollbackStatus={selectedReport.rollbackStatus}
              />
            </div>
          )}
          {selectedReport?.rollbackStatus && (
            <div className="mx-5 p-2 shadow-sm" style={{ backgroundColor: '#84b7d5ff' }}>
              <p className="text-lg" style={{ color: '#fff' }}>
                ⚠️ <strong>Important</strong>: The earlier report has been modified and is no longer valid. This updated report is the final and correct version.
              </p>
            </div>
          )}
        </section>

      {title && title !== "Authorize Report!" && checkPermissionByRole() && speechToTextAvailable && (<SpeechToText contentDiv="content-edit" />)}

        {title && (
          <footer
            className="flex justify-start gap-2 p-4 bg-white rounded-bl rounded-br"
            style={{
              maxWidth: '100%',
              margin: '0 auto',
              paddingTop: '6px',
              paddingLeft: '2.05rem',
              paddingBottom: '1.5rem'
            }}
          />
        )}
      </>
  );

  return (
    <ReactModal
      className={
        containerDimensions
          ? `relative outline-none w-4/5`
          : 'relative max-h-full w-11/12 outline-none lg:w-10/12 xl:w-9/12'
      }
      overlayClassName={
        movable
          ? 'fixed top-0 left-0 right-0 bottom-0 z-50 flex items-center justify-center py-16 pointer-events-none'
          : 'fixed top-0 left-0 right-0 bottom-0 z-50 bg-overlay flex items-center justify-center py-16'
      }
      shouldCloseOnEsc={shouldCloseOnEsc}
      onRequestClose={handleClose}
      isOpen={isOpen}
      title={title}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
    >
      <div
        style={{
          margin: 'auto',
          maxWidth: '90%',
          width: title === '' || title === 'Authorize Report!' ? '30rem' : '50rem',
        }}
      >
        {movable ? (
          <Draggable handle=".drag-handle" defaultClassName="bg-primary-dark pointer-events-auto">
            <div>{modalContent}</div>
          </Draggable>
        ) : (
          modalContent
        )}
      </div>
      <ConfirmationPopup
        isOpen={isPopupRedoOpen}
        title="Reason for Redo Scan"
        onConfirm={handleRedoConfirm}
        onCancel={handleRedoCancel}
        deleteMessage="Your study & report will be deleted."
        textField={
          <div className="w-full">
            <input
              type="text"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (errorMessage) setErrorMessage('');
              }}
              placeholder="Reason"
              className="textfield w-full"
            />
            {errorMessage && (
              <p className="text-red-500 text-base mt-1">
                {errorMessage}
              </p>
            )}
          </div>
        }
      />
      <Loader isLoaderOpen={isLoading} />
    </ReactModal>
  );
};

Modal.propTypes = {
  closeButton: PropTypes.bool,
  shouldCloseOnEsc: PropTypes.bool,
  isOpen: PropTypes.bool,
  title: PropTypes.string,
  onClose: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  shouldCloseOnOverlayClick: PropTypes.bool,
  movable: PropTypes.bool,
  containerDimensions: PropTypes.string,
  contentDimensions: PropTypes.string,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
};

export default Modal;
